import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { ProtectedRoute } from '@/auth/protected-route';
import { chatRoutes } from '@/features/chat';
import { contactRoutes } from '@/features/contact';
import { gemsProtectedRoutes, gemsPublicRoutes } from '@/features/gems';
import { landingRoutes } from '@/features/landing';
import { legalRoutes } from '@/features/legal';
import { notFoundRoutes } from '@/features/not-found';
import { settingsRoutes } from '@/features/settings';
import { shareRoutes } from '@/features/share';
import { AuthLayout } from '@/layouts/auth/auth-layout';
import { ChatLayout } from '@/layouts/chat/chat-layout';
import { LandingLayout } from '@/layouts/landing/landing-layout';
import { SettingsLayout } from '@/layouts/settings/settings-layout';

import { paths } from './paths';

const LoginPage = lazy(() =>
  import('@/auth/pages/login-page').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/auth/pages/register-page').then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/auth/pages/forgot-password-page').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/auth/pages/reset-password-page').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('@/auth/pages/verify-email-page').then((m) => ({ default: m.VerifyEmailPage })),
);

const lazyAuth = (node: React.ReactNode) => <Suspense fallback={null}>{node}</Suspense>;

const router = createBrowserRouter([
  // Public chrome — landing, marketing pages, gems list/detail, legal
  {
    path: paths.root,
    element: <LandingLayout />,
    children: [
      ...landingRoutes,
      ...contactRoutes,
      ...legalRoutes,
      ...gemsPublicRoutes,
    ],
  },

  // Auth screens — own minimal layout
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'login', element: lazyAuth(<LoginPage />) },
      { path: 'register', element: lazyAuth(<RegisterPage />) },
      { path: 'forgot-password', element: lazyAuth(<ForgotPasswordPage />) },
      { path: 'reset-password', element: lazyAuth(<ResetPasswordPage />) },
      { path: 'verify-email', element: lazyAuth(<VerifyEmailPage />) },
    ],
  },

  // Authenticated chat surface — `/c`, `/c/:id`, `/gems/:slug/c/:id`
  {
    element: (
      <ProtectedRoute>
        <ChatLayout />
      </ProtectedRoute>
    ),
    children: [...chatRoutes, ...gemsProtectedRoutes],
  },

  // Authenticated settings surface
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <SettingsLayout />
      </ProtectedRoute>
    ),
    children: settingsRoutes,
  },

  // Public shared-conversation view — `/share/:shareId`. Top-level and OUTSIDE
  // ProtectedRoute: a shared conversation is viewable without auth. The page
  // renders its own slim header, so it needs no layout wrapper.
  ...shareRoutes,

  // 404 — caught last; renders inside the public chrome so it feels like the rest of the site
  {
    path: paths.root,
    element: <LandingLayout />,
    children: notFoundRoutes,
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
